'use client';
import { ChatRouteLoading } from '@/components/MessageBoxLoading';
import ChatWindow from '@/components/predic/message/ChatWindow';
import { useRightDrawer } from '@/contexts/DrawerContext';
import { useHeader } from '@/contexts/HeaderContext';
import { useTranslation } from '@/lib/i18n';
import customFetch from '@/lib/utils/fetch';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAsyncEffect } from 'ahooks';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface ChatMianProps {
  chatId: string;
  isShared?: boolean;
  floating?: boolean;
  currentQuery?: string;
  onCurrentQueryConsumed?: () => void;
}
export default function ChatMain({
  chatId,
  isShared,
  floating = false,
  currentQuery,
  onCurrentQueryConsumed,
}: ChatMianProps) {
  const [chat, setChat] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { closeDrawer } = useRightDrawer();
  const { closePanel } = useNotificationStore();
  const { t } = useTranslation();

  const pathname = usePathname();

  const { setIsTransparent } = useHeader();

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useAsyncEffect(async () => {
    try {
      const responses = await Promise.all([
        customFetch(`/api/predic/${isShared ? 'share' : 'chat'}/${chatId}/message`),
        customFetch(`/api/predic/${isShared ? 'share' : 'chat'}/${chatId}`),
      ]);
      const data = await responses[1].json();
      const history = await responses[0].json();
      setChat({
        chat: data.data.chat,
        queryType: data.data.queryType,
        messages: history.conversations.map((item: any) => {
          if (item.role === 'assistant') {
            let content: any;
            try {
              content = JSON.parse(item.content);
            } catch {
              content = item.content;
            }
            const { thinkStatus, createdAt, webSources, audioSources } = parseMetadata(
              item.metadata,
            );
            return {
              ...item,
              content: typeof content === 'string' ? content : content.answer || '',
              thinkStatus,
              raw: content.raw || '',
              webSources,
              audioSources,
              createdAt,
              riskStatement: tRef.current(
                'Generated by AI. Not investment advice. Please assess risks independently.',
              ),
            };
          }
          return item;
        }),
      });
    } catch (error) {
      console.error('Failed to fetch chat:', error);
      setError((error as any).message || tRef.current('Failed to fetch chat'));
    } finally {
      setLoading(false);
    }
  }, [chatId, isShared]);

  useEffect(() => {
    setIsTransparent(false);
    if (pathname.startsWith('/predic/c/')) {
      closeDrawer();
      closePanel();
    }
  }, [setIsTransparent, pathname]);

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <ChatRouteLoading />
      </div>
    );
  }

  if (!loading && chat) {
    return (
      <ChatWindow
        chatId={chatId as string}
        query={chat.chat.title}
        history={chat.messages}
        isShared={isShared}
        floating={floating}
        queryType={chat.chat.queryType}
        currentQuery={currentQuery}
        onCurrentQueryConsumed={onCurrentQueryConsumed}
      />
    );
  }

  return (
    <div className="flex items-center justify-center h-100 text-xl">{t('chat not found')}</div>
  );
}

function parseMetadata(metadata: string) {
  try {
    console.log('metadata:', JSON.parse(metadata));
    const { thinkStatus, createdAt } = JSON.parse(metadata);
    // console.log('thinkStatus:', thinkStatus);
    const newThinkStatus: any[] = [];
    const webSources: any = [];
    const audioSources: string[] = [];
    for (let i = 0; i < thinkStatus.length; i++) {
      if (thinkStatus[i].type.startsWith('tool_') && thinkStatus[i].data !== 'task') {
        const existingIndex = newThinkStatus.findIndex(
          (item: any) => item.task === thinkStatus[i].data,
        );

        const _thinkStatus = {
          task: thinkStatus[i].data,
          status: thinkStatus[i].type === 'tool_end' ? 'done' : 'doing',
          agent: thinkStatus[i].agent,
          createdAt: new Date(),
          metadata: thinkStatus[i].metadata,
          toolCallId: thinkStatus[i].metadata?.tool_call_id,
          description: thinkStatus[i].metadata?.description || '',
        };

        if (existingIndex >= 0) {
          // console.log('Updating existing thinkStatus for task:', _thinkStatus);
          // 更新现有的 think 状态
          newThinkStatus[existingIndex] = {
            ..._thinkStatus,
            description: newThinkStatus[existingIndex].description || _thinkStatus.description,
          };
        } else {
          // 添加新的 think 状态
          newThinkStatus.push(_thinkStatus);
        }
      } else if (thinkStatus[i].type.endsWith('_thinking')) {
        const existingIndex = newThinkStatus.findIndex(
          (item: any) => item.task === 'thinking' && item.agent === thinkStatus[i].agent,
        );
        const _thinkStatus = {
          task: 'thinking',
          status: 'doing',
          agent: thinkStatus[i].agent,
          createdAt: new Date(),
          metadata: thinkStatus[i].metadata,
          toolCallId: thinkStatus[i].metadata?.tool_call_id,
          content: thinkStatus[i].data || '',
        };

        if (existingIndex >= 0) {
          // console.log('Updating existing thinkStatus for task:', _thinkStatus);
          // 更新现有的 think 状态
          newThinkStatus[existingIndex] = {
            ..._thinkStatus,
            content: newThinkStatus[existingIndex].content + _thinkStatus.content,
          };
        } else {
          // 添加新的 think 状态
          newThinkStatus.push(_thinkStatus);
        }
      } else if (thinkStatus[i].type === 'reference_data') {
        webSources.push(
          ...(Array.isArray(thinkStatus[i].data) ? thinkStatus[i].data : [thinkStatus[i].data]),
        );
      } else if (thinkStatus[i].type === 'answer_paragraph') {
        audioSources.push(thinkStatus[i].data);
      }
    }
    console.log('webSources:', webSources);
    console.log('audioSources:', audioSources);
    return { thinkStatus: newThinkStatus, createdAt, webSources, audioSources };
  } catch (e) {
    return { thinkStatus: [], createdAt: null, webSources: [] };
  }
}
